/**
 * SIL-611 — Regulatory register (Reports tab) + inline regulation chips.
 * Every string below is copied VERBATIM from the legally signed-off copy
 * doc, `resources/plans/sil-611-regulation-reference-copy.md` (approved
 * 2026-08-10 by Jarriett Robinson) — never reworded, never improvised. Any
 * change to a user-facing string here requires a new sign-off recorded in
 * that doc, following the SIL-426 / SIL-459 register convention.
 *
 * Four ROWS (three regulations + the reference-only penalty-basis row), but
 * only THREE of them carry an inline chip — the penalty-basis row (40 CFR
 * 19.4) is reference-only and has no chip (SIL-611 decision 4).
 */

export interface RegulatoryRegisterRow {
  /** Stable key so inline chips can reference a row without duplicating
   * its copy. `penalty_basis` has no corresponding chip. */
  key: 'universal_waste_battery' | 'ca_uwed' | 'calrecycle_battery' | 'penalty_basis';
  regulation: string;
  jurisdiction: string;
  appliesTo: string;
  referenceLabel: string;
  referenceUrl: string;
  /** Only the penalty-basis row carries a badge. */
  badge?: string;
}

export const REGULATORY_REGISTER_ROWS: RegulatoryRegisterRow[] = [
  {
    key: 'universal_waste_battery',
    regulation: 'Universal Waste Rule - batteries',
    jurisdiction: 'US · EPA',
    appliesTo:
      'Batteries in retired equipment - backup and CMOS cells routed through the Cat-5 disposal flow.',
    referenceLabel: '40 CFR 273',
    referenceUrl: 'https://www.ecfr.gov/current/title-40/chapter-I/subchapter-I/part-273?toc=1',
  },
  {
    key: 'ca_uwed',
    regulation: 'Universal waste electronic devices',
    jurisdiction: 'California · DTSC',
    appliesTo:
      'California-sited equipment containing circuitry - servers, GPU nodes, and harvested boards - when routed to disposal rather than resale.',
    referenceLabel: 'CA UWED',
    referenceUrl: 'https://dtsc.ca.gov/e-waste-more-information/',
  },
  {
    key: 'calrecycle_battery',
    regulation: 'Embedded-battery requirements',
    jurisdiction: 'California · CalRecycle',
    appliesTo: 'Battery-embedded products entering the component-harvest and repurpose paths.',
    referenceLabel: 'CalRecycle',
    referenceUrl: 'https://calrecycle.ca.gov/electronics/embeddedbatteries/',
  },
  {
    key: 'penalty_basis',
    regulation: 'Civil penalty adjustments',
    jurisdiction: 'US · EPA',
    badge: 'Penalty basis',
    appliesTo:
      'The statutory maximum civil penalties EPA may assess for mishandled waste, adjusted annually for inflation. Reference only - no penalty exposure is computed for your fleet.',
    referenceLabel: '40 CFR 19.4',
    referenceUrl: 'https://www.ecfr.gov/current/title-40/chapter-I/subchapter-A/part-19/section-19.4',
  },
];

/** Regulation keys that carry an inline chip — every row except the
 * reference-only penalty-basis row. */
export type RegulationChipKey = Exclude<RegulatoryRegisterRow['key'], 'penalty_basis'>;

/** §3 — register intro line. */
export const REGULATORY_REGISTER_INTRO =
  'Disposal and end-of-life handling of retired hardware is regulated. The rules below are the ones that govern the flows shown on this dashboard. SiliconX holds no determination of your compliance with any of them, and the figures on this page are carbon estimates, not compliance evidence.';

/** §4 — register heading + anchor. */
export const REGULATORY_REGISTER_HEADING = 'Regulatory register';
export const REGULATORY_REGISTER_HEADING_WITH_COUNT = `${REGULATORY_REGISTER_HEADING} (${REGULATORY_REGISTER_ROWS.length})`;
export const REGULATORY_REGISTER_ANCHOR_ID = 'regulatory-register';

/** §5 — cross-link line (EnvironmentalFooter + Carbon Planning). */
export const REGULATORY_CROSS_LINK_LINE =
  'Disposal and end-of-life flows are subject to regulation - see the Regulatory register →';

/** §6 — accessible-name template for a chip link. */
export function regulationChipAccessibleName(label: string): string {
  return `${label} - opens the regulation text in a new tab`;
}
